import React from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@material-ui/core'
import { Close } from '@material-ui/icons'

const parsePreview = (raw) => {
  if (!raw) return []
  return raw.trim().split('\n').map(line => {
    const cols = line.split('\t').map(c => c.trim())
    if (cols.length < 2) return null
    const isNum = /^\d+\.?\d*$/.test(cols[0].replace(/,/g, ''))
    if (!isNum) return null
    return {
      itemNo: parseFloat(cols[0]),
      description: cols[1] || '',
      unit: (cols[2] || '').trim(),
      quantity: parseFloat((cols[3] || '0').replace(/,/g, '')) || 0
    }
  }).filter(Boolean)
}

const BulkImportModal = ({ open, onClose, onImport, project, url }) => {
  const [building, setBuilding] = React.useState('')
  const [section, setSection] = React.useState('')
  const [rawData, setRawData] = React.useState('')
  const [preview, setPreview] = React.useState([])
  const [showPreview, setShowPreview] = React.useState(false)
  const [importing, setImporting] = React.useState(false)
  const [result, setResult] = React.useState(null)

  React.useEffect(() => {
    if (open && project?.buildings?.length) {
      setBuilding(project.buildings[0].name)
    }
  }, [open, project])

  const handleClose = () => {
    setRawData('')
    setPreview([])
    setShowPreview(false)
    setResult(null)
    setSection('')
    onClose()
  }

  const handlePreview = () => {
    const parsed = parsePreview(rawData)
    setPreview(parsed)
    setShowPreview(true)
  }

  const handleImport = () => {
    if (!building) return
    setImporting(true)
    const projectId = project?._id
    axios.post(`${url}/api/bid-estimation/projects/${projectId}/line-items/bulk-import`, {
      building,
      rawData,
      section: section.trim() || undefined
    }).then(res => {
      const count = res.data?.imported ?? res.data?.length ?? preview.length
      setResult(`Imported ${count} items`)
      setImporting(false)
      setTimeout(() => {
        handleClose()
        onImport()
      }, 1500)
    }).catch(err => {
      console.error(err)
      setResult('Import failed: ' + (err.response?.data?.message || err.message))
      setImporting(false)
    })
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6">Bulk Import Line Items</Typography>
          </Grid>
          <Grid item>
            <IconButton size="small" onClick={handleClose}><Close /></IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={6}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Building</InputLabel>
              <Select value={building} onChange={e => setBuilding(e.target.value)} label="Building">
                {(project?.buildings || []).map(b => (
                  <MenuItem key={b.name} value={b.name}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Default Section (optional)"
              value={section}
              onChange={e => setSection(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>

        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
          Paste tab-separated rows from a spreadsheet. Expected columns: Item #, Description, Unit, Quantity
        </Typography>

        <TextField
          multiline
          rows={15}
          value={rawData}
          onChange={e => { setRawData(e.target.value); setShowPreview(false); setResult(null) }}
          fullWidth
          variant="outlined"
          placeholder={'1\tRemove existing carpet\tSF\t5000\n2\tInstall new tile\tSF\t5000'}
        />

        {showPreview && preview.length > 0 && (
          <Paper variant="outlined" style={{ marginTop: 16, maxHeight: 300, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item #</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.itemNo}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{row.unit}</TableCell>
                    <TableCell align="right">{row.quantity.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="body2" style={{ padding: 8 }}>
              {preview.length} items parsed
            </Typography>
          </Paper>
        )}

        {showPreview && preview.length === 0 && (
          <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
            No valid rows found. Ensure data is tab-separated with a numeric first column.
          </Typography>
        )}

        {result && (
          <Typography
            variant="body2"
            style={{ marginTop: 12, color: result.startsWith('Import failed') ? '#c62828' : '#2e7d32', fontWeight: 600 }}
          >
            {result}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>Cancel</Button>
        {!showPreview ? (
          <Button onClick={handlePreview} variant="outlined" color="primary" disabled={!rawData.trim()}>
            Preview
          </Button>
        ) : (
          <Button
            onClick={handleImport}
            variant="contained"
            color="primary"
            disabled={importing || preview.length === 0 || !building}
          >
            {importing ? <CircularProgress size={24} /> : `Import ${preview.length} Items`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default BulkImportModal
